import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormCard } from '@qbe/ads-blocks-forms';
import { FormStepContentProps } from 'types';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { Form } from 'components';
import { useContentContext } from '@qbe/dfc-content';

import validationSchema, { personalDetailsSchema } from './schema';
import ContactDetails from './sections/ContactDetails';
import PersonalDetails from './sections/PersonalDetails';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';
import { TrackEvent, formatNext } from 'hooks/useAnalytics';

const InsuredDetailsForm = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();
  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const form = useForm<FormDataType>({
    mode: 'onBlur',
    defaultValues: formData,
    resolver: yupResolver(validationSchema) as any,
  });

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  const {
    handleSubmit,
    watch,
    getValues,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const values = watch();
  const isValid = validationSchema.isValidSync(watch());

  const [isContactDetailsDisplayed, setIsContactDetailsDisplayed] = React.useState(false);
  const [showBtnContactDetails, setShowBtnContactDetails] = React.useState(true);

  const formRef = useRef<HTMLFormElement>(null);

  const isPersonalDetailsValid = personalDetailsSchema.isValidSync(values);
  const [showPersonalDetailsButton, setShowPersonalDetailsButton] = React.useState(true);

  const handleSubmitPersonalDetails = () => {
    setShowPersonalDetailsButton(false);
    setShowBtnContactDetails(true);
    setIsContactDetailsDisplayed(true);
    TrackEvent(formatNext('nextContactDetails'), getValues());
  };

  return (
    <Form onSubmit={handleSubmit(onComplete)} ref={formRef} data-test="consent-form">
      <FormCard variant="pageTitle" title={getTextContent('fha.contact.details.stepHeader')} description="" />
      <PersonalDetails
        form={form}
        isFieldRequired={isFieldRequired}
        enableNextButton={isPersonalDetailsValid}
        showNextButton={showPersonalDetailsButton}
        onSubmit={handleSubmitPersonalDetails}
      />

      <ContactDetails
        form={form}
        show={isContactDetailsDisplayed}
        showNextButton={showBtnContactDetails}
        isContactDetailsValid={isValid}
        isFieldRequired={isFieldRequired}
      />
    </Form>
  );
};

export default InsuredDetailsForm;
